/**
 * Tab Processos - Página principal com KPIs e gráficos de processos
 * Replica a funcionalidade do backup dashboard#processos
 */
import { ProcessosStatusChart, PendenciasFornecedorChart, ProcessosFilters, PendencyFilters } from './components';
import {
  useProcessesKpis,
  useProcessesByTypeStatus,
  useProcessesByPendency,
} from './hooks';
import { useProcessosFilters } from './hooks/useProcessosFilters';
import { ClipboardList, Clock, AlertTriangle, Download, Loader2, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { ExcelPreviewDialog, type PreviewSheet } from '@/components/ExcelPreviewDialog';

// KPI Card simples para Processos (reutilizando estrutura)
interface ProcessosKpiCardProps {
  title: string;
  total: number;
  icon: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  color?: 'default' | 'blue' | 'red';
}

function ProcessosKpiCard({
  title,
  total,
  icon,
  isLoading,
  className,
  color = 'default',
}: ProcessosKpiCardProps) {
  const colorClasses = {
    default: '',
    blue: 'border-blue-500/30 bg-blue-50/50 dark:bg-blue-950/20',
    red: 'border-red-500/30 bg-red-50/50 dark:bg-red-950/20',
  };

  if (isLoading) {
    return (
      <div className={cn('bg-card rounded-lg border p-4 animate-pulse', className)}>
        <div className="h-4 bg-muted rounded w-24 mb-2" />
        <div className="h-8 bg-muted rounded w-16" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-card rounded-lg border p-4 transition-all hover:shadow-md flex flex-col',
        colorClasses[color],
        className
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        <span className="text-muted-foreground">{icon}</span>
      </div>
      <div className="text-3xl font-bold">{total.toLocaleString('pt-BR')}</div>
    </div>
  );
}

export function ProcessosTab() {
  // Estado para loading da exportação
  const [exportLoading, setExportLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewSheets, setPreviewSheets] = useState<PreviewSheet[]>([]);
  
  // Hooks de dados
  const { data: kpis, isLoading: loadingKpis } = useProcessesKpis();
  const { data: typeStatus, isLoading: loadingTypeStatus } = useProcessesByTypeStatus();
  const { data: pendencies, isLoading: loadingPendencies } = useProcessesByPendency();
  
  // Filtros atuais para passar para o modal de detalhes
  const { regional, uf, docTypes, suppliers } = useProcessosFilters();

  // Fetch de dados de pendências detalhadas (reutilizado por export e preview)
  const fetchPendencyItems = useCallback(async () => {
    if (!pendencies?.rows?.length) return [];
    const allItems: Array<{
      pendencyType: string;
      unitIdentification: string;
      regional: string;
      cnpj: string;
      address: string;
      supplier: string;
    }> = [];
    for (const row of pendencies.rows) {
      const params = new URLSearchParams();
      params.set('pendencyType', row.pendencyType);
      if (regional) params.set('regional', regional);
      if (uf) params.set('uf', uf);
      if (docTypes?.length) params.set('docTypes', docTypes.join(','));
      if (suppliers?.length) params.set('suppliers', suppliers.join(','));
      const response = await apiFetch<{
        status: string;
        result: {
          pendencyType: string;
          items: Array<{
            unitIdentification: string;
            regional: string;
            cnpj: string;
            address: string;
            supplier: string;
          }>;
        };
      }>(`/claro/api/processes/pendency-details?${params.toString()}`);
      if (response?.result?.items) {
        response.result.items.forEach(item => {
          allItems.push({
            pendencyType: row.pendencyType,
            ...item,
          });
        });
      }
    }
    return allItems;
  }, [pendencies, regional, uf, docTypes, suppliers]);

  const itemsToRows = (items: Awaited<ReturnType<typeof fetchPendencyItems>>) =>
    items.map(item => ({
      'Pendência': item.pendencyType,
      'ID Unidade': item.unitIdentification,
      'Regional': item.regional,
      'CNPJ': item.cnpj,
      'Endereço': item.address,
      'Fornecedor': item.supplier,
    }));

  const PENDENCY_HEADERS = ['Pendência', 'ID Unidade', 'Regional', 'CNPJ', 'Endereço', 'Fornecedor'];

  // Preview handler
  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const allItems = await fetchPendencyItems();
      const rows = itemsToRows(allItems);
      setPreviewSheets([{
        key: 'pendencias',
        label: '📋 Pendências Detalhadas',
        color: 'text-blue-700',
        bg: 'bg-blue-100 border-blue-400',
        headers: PENDENCY_HEADERS,
        rows,
      }]);
      setShowPreview(true);
    } catch (error) {
      console.error('Erro ao carregar preview:', error);
      alert('Erro ao carregar dados. Tente novamente.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Download handler (exportar Excel)
  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allItems = await fetchPendencyItems();
      const rows = itemsToRows(allItems);
      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet['!cols'] = [
        { wch: 35 }, { wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 50 }, { wch: 20 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Pendências Detalhadas');
      XLSX.writeFile(workbook, `pendencias-detalhadas-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      alert('Erro ao exportar dados. Tente novamente.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 md:gap-4 p-0">
      {/* Header com filtros */}
      <div className="flex flex-wrap items-center justify-between gap-3 md:gap-4">
        <ProcessosFilters />
      </div>

      {/* Grid principal - responsivo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-18 gap-3 md:gap-4">
        {/* Linha 1: KPIs */}
        <div className="col-span-1 lg:col-span-6 h-auto lg:h-[120px]">
          <ProcessosKpiCard
            title="Total de Processos"
            total={kpis?.total || 0}
            icon={<ClipboardList className="w-5 h-5" />}
            isLoading={loadingKpis}
            className="h-full"
          />
        </div>
        <div className="col-span-1 lg:col-span-6 h-auto lg:h-[120px]">
          <ProcessosKpiCard
            title="Em Andamento"
            total={kpis?.andamento || 0}
            icon={<Clock className="w-5 h-5 text-blue-500" />}
            isLoading={loadingKpis}
            className="h-full"
            color="blue"
          />
        </div>
        <div className="col-span-1 sm:col-span-2 lg:col-span-6 h-auto lg:h-[120px]">
          <ProcessosKpiCard
            title="Atrasados"
            total={kpis?.atrasado || 0}
            icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
            isLoading={loadingKpis}
            className="h-full"
            color="red"
          />
        </div>

        {/* Linha 2: Gráfico Tipo x Status (span completo) */}
        <div className="col-span-1 sm:col-span-2 lg:col-span-18">
          <ProcessosStatusChart
            data={typeStatus?.rows || []}
            statuses={typeStatus?.statuses || []}
            isLoading={loadingTypeStatus}
          />
        </div>

        {/* Linha 3: Gráfico Pendências por Tipo x Fornecedor (span completo) */}
        <div className="col-span-1 sm:col-span-2 lg:col-span-18">
          <div className="bg-card rounded-lg border p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold">Pendências por Tipo</h3>
              <div className="flex items-center gap-2">
                <PendencyFilters 
                  availablePendencyTypes={pendencies?.availableFilters?.pendencyTypes || []}
                  availableSuppliers={pendencies?.availableFilters?.suppliers || []}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreview}
                  disabled={!pendencies?.rows?.length || previewLoading}
                >
                  {previewLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4 mr-2" />
                  )}
                  {previewLoading ? 'Carregando...' : 'Pré-visualizar'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportExcel}
                  disabled={!pendencies?.rows?.length || exportLoading}
                >
                  {exportLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  {exportLoading ? 'Exportando...' : 'Exportar Excel'}
                </Button>
              </div>
            </div>
            <PendenciasFornecedorChart
              data={pendencies?.rows || []}
              suppliers={pendencies?.suppliers || []}
              isLoading={loadingPendencies}
              filters={{
                regional: regional ?? undefined,
                uf: uf ?? undefined,
                docTypes: docTypes?.join(','),
                suppliers: suppliers?.join(','),
              }}
            />
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <ExcelPreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        sheets={previewSheets}
        filename={`pendencias-detalhadas-${new Date().toISOString().split('T')[0]}.xlsx`}
        onDownload={handleExportExcel}
        isDownloading={exportLoading}
      />
    </div>
  );
}
